<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useOutboundDeliveryStore } from '../stores/outboundDeliveryStore.ts';
import { useRefreshOutboundDeliveryStore } from '../stores/refreshOutboundDeliveryStore.ts';
import { useListOutboundDeliveryQueryApi } from '../api/listOutboundDeliveryQueryApi';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useRouter } from 'vue-router';
import { useOutboundDeliveryAuditApi } from '@/audit/api/outboundDeliveryApi';
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import DateRangeFilter from '@/reports/reports/shared/DateRangeFilter.vue';
import { MultiSelect } from 'primevue';
import { useSortByName } from '@/common/composables/sortByName';
import type { OutboundDelivery } from '../domain/outboundDelivery.ts';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import type { MenuItem } from 'primevue/menuitem';
import { Button, DataTable, Column, InputText, Menu, Card, type DataTableRowClickEvent, type DataTableRowDoubleClickEvent } from 'primevue';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import DeleteOutboundDeliveryDialog from './DeleteOutboundDeliveryDialog.vue';
import OutboundDeliveryTransactionsDialog from './OutboundDeliveryTransactionsDialog.vue';
import { DeliveryStatus } from 'shared/deliveries/domain/deliveryStatus.ts';
import type { EntityChanged } from 'shared/common/composables/entityStore';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const store = useOutboundDeliveryStore();
const { list } = storeToRefs(store);
const refreshStore = useRefreshOutboundDeliveryStore();
const router = useRouter();
const auditApi = useOutboundDeliveryAuditApi();

const displayedList = ref<OutboundDelivery[]>([]);
const queryActive = ref(false);
const deliveries = useSortByName({
  list: computed(() => (queryActive.value ? displayedList.value : list.value)),
  getName: (e) => e.name,
});

const selected = ref<OutboundDelivery[]>([]);

interface DeliveryColumn extends ColumnField {
  sortable: boolean;
  sortField?: string;
  cellClass?: string;
  kind: 'text' | 'status';
}

const columnDefinitions = computed<DeliveryColumn[]>(() => [
  { field: 'name', header: translations.value.generic.name, sortable: true, sortField: 'nameSortOrder', cellClass: 'outbound-delivery-name', kind: 'text', filterType: 'text' },
  {
    field: 'status',
    header: translations.value.generic.status,
    sortable: true,
    cellClass: 'outbound-delivery-customer',
    kind: 'status',
    filterType: 'select',
    showFilterMatchModes: false,
    showFilterOperator: false,
    filterOptions: [
      DeliveryStatus.Planned,
      DeliveryStatus.Released,
      DeliveryStatus.Active,
      DeliveryStatus.Finished,
      DeliveryStatus.Invoiced,
    ].map((s) => ({ label: translations.value.enums.deliveryStatus(s), value: s })),
  },
  { field: 'customer', header: translations.value.generic.customer, sortable: true, cellClass: 'outbound-delivery-customer', kind: 'text', filterType: 'text' },
]);
const { orderedColumns, selectedColumns, setSelectedColumns, setOrder, reset: resetColumns } =
  useColumnSelector<DeliveryColumn>({ storageKey: 'outbound-delivery-table-columns', columns: columnDefinitions });

const { filters, filteredCount, hasActiveFilters, onFilter } = useColumnFilters({
  columns: columnDefinitions,
  totalCount: () => deliveries.value.length,
});

const localQueryLoading = ref(false);
const loading = computed(() => (refreshStore.loading || localQueryLoading.value));

const dateRangeRef = ref<any | null>(null);
const selectedStatuses = ref<DeliveryStatus[]>([]);
const statusOptions = computed(() => {
  const list = [
    DeliveryStatus.Planned,
    DeliveryStatus.Released,
    DeliveryStatus.Active,
    DeliveryStatus.Finished,
    DeliveryStatus.Invoiced,
  ];
  return list.map((t) => ({ value: t, name: translations.value.enums.deliveryStatus(t) }));
});
const menuEntityId = ref<string>();

const menu = useTemplateRef('menu');
const deleteDialog = useTemplateRef('delete-dialog');
const auditDialog = useTemplateRef('audit-dialog');
const transactionDialog = useTemplateRef('transaction-dialog');
const actionFailedAlert = useActionFailedAlert();

onMounted(() => {
  store.changed.on(onDeliveriesChanged);
  refreshStore.initialize();
});

onUnmounted(() => {
  store.changed.off(onDeliveriesChanged);
});

function onDeliveriesChanged(changes: EntityChanged<OutboundDelivery>[]) {
  if (!queryActive.value) return;

  for (const change of changes) {
    const index = displayedList.value.findIndex((delivery) => delivery.id === change.id);
    if (index < 0) continue;

    if (change.action === 'removed') {
      displayedList.value.splice(index, 1);
    } else {
      displayedList.value[index] = change.data;
    }
  }
}

function validateAndBuildQuery() {
  const dv = dateRangeRef.value?.getFilterValues?.() ?? { from: '', to: '' };
  if (!dv.from || !dv.to) return null;

  return {
    from: dv.from,
    to: dv.to,
  };
}

async function onQueryClick() {
  const q = validateAndBuildQuery();
  if (!q) return;

  const queryApi = useListOutboundDeliveryQueryApi();
  try {
    localQueryLoading.value = true;
    const response = await queryApi.execute(q);
    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return;
    }

    const tmpDeliveries = response.deliveries as unknown as OutboundDelivery[];
    displayedList.value = tmpDeliveries.filter((delivery) => selectedStatuses.value.length === 0 || selectedStatuses.value.includes(delivery.status));
    queryActive.value = true;
  } finally {
    localQueryLoading.value = false;
  }
}

async function onResetClick() {
  dateRangeRef.value && dateRangeRef.value.reset();
  selectedStatuses.value = [];
  displayedList.value = [];
  queryActive.value = false;
  await refreshStore.initialize();
}

const menuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const id = menuEntityId.value;
      if (!id) return;
      show(id);
    },
  },
  {
    label: translations.value.outboundDelivery.listPage.transactions,
    icon: 'pi pi-list',
    command() {
      const id = menuEntityId.value;
      if (!id) return;
      transactionDialog.value?.show(id);
    }
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const id = menuEntityId.value;
      if (!id) return;
      deleteDialog.value?.show(id);
    },
    disabled: () => {
      const id = menuEntityId.value;
      if (!id) return true;

      const delivery = deliveries.value.find((delivery) => delivery.id === id);
      if (!delivery) return true;
      for (const row of delivery.rows) {
        if ((row.deliveredWeight ?? 0) > 0 || (row.deliveredVolume ?? 0) > 0) {
          return true;
        }
      }

      return false;
    }
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const id = menuEntityId.value;
      if (!id) return;

      const response = await auditApi.execute({
        id: id,
      });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({
        audit: response.audit,
      });
    },
  },
]);

function onDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const outboundDelivery = ev.data as OutboundDelivery;
  show(outboundDelivery.id);
}

function onClick(ev: DataTableRowClickEvent) {
  const target = ev.originalEvent.target as HTMLElement | null;
  if (!target) return;
  const name = target.closest('.outbound-delivery-name');
  if (!name) return;
  show(ev.data.id);
}

function onAddNewClick() {
  show(null);
}

function show(id: string | null) {
  if (id) {
    router.push('/delivery/outbound/' + id);
  } else {
    router.push('/delivery/outbound');
  }
}

function toggleMenu(ev: Event, id: string) {
  menuEntityId.value = id;
  menu.value?.toggle(ev);
}
</script>

<template>
  <Card class="outbound-delivery-card">
    <template #title> 
      <div class="filter-controls">
        <DateRangeFilter ref="dateRangeRef" />
        <div class="inline-block ms-3">
          <span class="me-1">{{ translations.generic.status }}</span>
          <MultiSelect v-model="selectedStatuses" :options="statusOptions" optionLabel="name" optionValue="value"
            display="chip" filter class="w-15rem" :maxSelectedLabels="1"></MultiSelect>
        </div>
        <Button class="ms-3" label="Query" @click="onQueryClick"></Button>
        <Button class="ms-2" :label="translations.dataTable.clearFilters" @click="onResetClick"></Button>
      </div>
      <span v-if="hasActiveFilters && filteredCount < deliveries.length" class="filtered-count">
          ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ deliveries.length }})
      </span>
      <Button class="add-button" @click="onAddNewClick" :label="translations.generic.add">
      </Button>
      <InputText class="search-field" autofocus v-model="filters.global.value"
        :placeholder="translations.generic.search" />
    </template>
    <template #content>
      <DataTable class="delivery-table" sort-field="name" v-model:selection="selected" v-model:filters="filters"
        :value="deliveries" data-key="id" scrollable scrollHeight="flex" meta-key-selection filterDisplay="menu" @filter="onFilter"
        @row-dblclick="onDoubleClick" :loading="loading" @row-click="onClick">
        <template #empty>{{ translations.outboundDelivery.listPage.noFound }}</template>
        <template #loading>{{ translations.outboundDelivery.listPage.loading }}</template>
        <Column v-for="col of selectedColumns" :key="col.field" :field="col.field" :class="col.cellClass"
          :header="col.header" :sortable="col.sortable" :sort-field="col.sortField"
          :filter="!!col.filterType" :filterField="col.filterField ?? col.field"
          :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
          :dataType="col.filterType === 'numeric' ? 'numeric' : undefined">
          <template v-if="col.kind === 'status'" #body="columnData">
            {{ translations.enums.deliveryStatus(columnData.data.status) }}
          </template>
          <template v-if="col.filterType" #filter="{ filterModel, filterCallback }">
            <ColumnFilterCell :filter-type="col.filterType" :filter-model="filterModel" :filter-callback="filterCallback"
              :options="col.filterOptions"
              :placeholder="translations.dataTable.filterBy + ' ' + col.header.toLocaleLowerCase()" />
          </template>
        </Column>
        <Column class="w-24 text-end!">
          <template #header>
            <ColumnSelectorButton :columns="orderedColumns" :modelValue="selectedColumns"
              @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="resetColumns" />
          </template>
          <template #body="OutboundDelivery">
            <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
              @click="(ev) => toggleMenu(ev, OutboundDelivery.data.id)"></Button>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>

  <DeleteOutboundDeliveryDialog ref="delete-dialog"></DeleteOutboundDeliveryDialog>
  <OutboundDeliveryTransactionsDialog ref="transaction-dialog"></OutboundDeliveryTransactionsDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="menu" :model="menuItems" :popup="true"></Menu>
</template>

<style scoped>
:deep(.p-datatable-hoverable .p-datatable-selectable-row) {
  cursor: unset;
}

:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto 1rem auto;
  column-gap: 0.5rem;
  align-items: center;
}

.filtered-count { 
  justify-self: right;
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.add-button {
  grid-column: 3;
}

.search-field {
  grid-column: 5;
}

:deep(.outbound-delivery-name) {
  cursor: pointer;
}

:deep(.outbound-delivery-name:hover) {
  color: var(--p-primary-900);
  background-color: var;
  text-decoration: underline;
}

.outbound-delivery-card {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}
</style>
